import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TrendingUp, LineChart, GitCompareArrows } from 'lucide-react';
import CategoryPriceTrendCard from './CategoryPriceTrendCard';
import SkuTrendCompare from './SkuTrendCompare';

interface TrendsPanelProps {
  tableName: string;
  /** PT-#58 — Abre el modal combinado de filtros guardados (guardar + cargar) para Tendencias. */
  onOpenSavedFilters?: () => void;
  /** Deshabilita el botón mientras cargan los filtros guardados. */
  loadingSavedFilters?: boolean;
}

type TrendsTab = 'category' | 'skus';

const TrendsPanel: React.FC<TrendsPanelProps> = ({ tableName, onOpenSavedFilters, loadingSavedFilters = false }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TrendsTab>('category');

  if (!tableName) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="text-center py-12">
          <TrendingUp className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            {t('trends.title')}
          </h2>
          <p className="text-gray-600">
            {t('dashboard.select_data_set')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tabs: Por categoría / Comparar SKUs */}
      <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg max-w-md">
        <button
          onClick={() => setActiveTab('category')}
          className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-md transition-colors text-sm font-medium ${
            activeTab === 'category'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          <LineChart className="h-4 w-4" />
          <span>Por categoría</span>
        </button>
        <button
          onClick={() => setActiveTab('skus')}
          className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-md transition-colors text-sm font-medium ${
            activeTab === 'skus'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          <GitCompareArrows className="h-4 w-4" />
          <span>Comparar Grupos</span>
        </button>
      </div>

      {/* PT-#58 — El acceso a Filtros Guardados va DENTRO del header del FilterBox de
          CategoryPriceTrendCard (mismo lugar que Publicaciones/Estadísticas), no suelto en
          la barra de tabs. "Comparar Grupos" aún no persiste ejes → no recibe el opener. */}
      {activeTab === 'category' ? (
        <CategoryPriceTrendCard
          tableName={tableName}
          onOpenSavedFilters={onOpenSavedFilters}
          loadingSavedFilters={loadingSavedFilters}
        />
      ) : (
        <SkuTrendCompare tableName={tableName} />
      )}
    </div>
  );
};

export default TrendsPanel;
